/* Modules — handover §14 step 10.
 *
 * Turning a section off takes it out of the sidebar (AppShell's nav filter).
 * For a long time it did not: this screen wrote a flag nothing read, so the
 * switches moved and the sidebar did not, which is a worse state than not
 * having the screen.
 *
 * It hides; it does not revoke, and the difference matters enough to say on the
 * screen rather than in a doc nobody opens: the SharePoint permission is the
 * boundary, and a disabled module with a live permission behind it is still
 * readable by anyone who knows the URL.
 *
 * Which makes this a tidying tool, not a security one — worth having, because a
 * sidebar with six sections nobody uses is a sidebar people stop reading.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import type { IModule } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Badge, EmptyState, LoadError, Panel, TableSkeleton } from '../ui';
import s from './screens.module.scss';

export function ModulesScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const modules = useQuery({ queryKey: qk.modules, queryFn: () => svc.access.modules() });

  const setEnabled = useMutation({

    meta: { ok: 'Module updated' },
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      svc.access.setModuleEnabled(id, enabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.modules });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  /* Grouped the way the sidebar is, so this screen reads as a picture of the
     thing it controls rather than as an alphabetical list of switches. */
  const groups = useMemo(() => {
    const out = new Map<string, IModule[]>();
    for (const m of modules.data ?? []) {
      const list = out.get(m.group);
      if (list) list.push(m);
      else out.set(m.group, [m]);
    }
    return [...out.entries()];
  }, [modules.data]);

  const off = (modules.data ?? []).filter((m) => !m.enabled).length;

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Modules</h1>
          <p>
            Which sections this tenant uses. {off > 0 ? `${off} turned off.` : 'All turned on.'}
          </p>
        </div>
      </div>

      {modules.isError && <LoadError error={modules.error} what="modules" />}

      <div className={s.ownedNote}>
        <i className="ti ti-eye-off" aria-hidden />
        <span>
          Turning a module off hides it from the sidebar. It does not revoke access — the data
          stays where it is and anyone with the SharePoint permission can still reach it. This is
          for keeping the navigation honest, not for locking anything down.
        </span>
      </div>

      {modules.isLoading ? (
        <Panel flush>
          <TableSkeleton rows={6} />
        </Panel>
      ) : groups.length === 0 ? (
        <Panel flush>
          <EmptyState icon="ti-puzzle" title="No modules defined" />
        </Panel>
      ) : (
        <div className={s.stack}>
          {groups.map(([group, items]) => (
            <Panel
              key={group}
              title={group}
              sub={`${items.filter((m) => m.enabled).length} of ${items.length} on`}
              flush
            >
              <div className={s.rows}>
                {items.map((m) => (
                  <div key={m.id} className={s.row}>
                    <span className={s.rowIcon}>
                      <i className={`ti ${m.icon}`} aria-hidden />
                    </span>
                    <div className={s.rowMain}>
                      <div className={s.rowTitle}>{m.label}</div>
                      <div className={s.rowSub}>
                        {m.enabled ? 'Shown in the sidebar' : 'Hidden — the data is still there'}
                      </div>
                    </div>
                    <Badge tone={m.enabled ? 'ok' : 'muted'}>{m.enabled ? 'On' : 'Off'}</Badge>
                    <label className={s.checkRow}>
                      <input
                        type="checkbox"
                        checked={m.enabled}
                        disabled={!online || setEnabled.isPending}
                        aria-label={`${m.enabled ? 'Disable' : 'Enable'} ${m.label}`}
                        onChange={(e) =>
                          setEnabled.mutate({ id: m.id, enabled: e.target.checked })
                        }
                      />
                    </label>
                  </div>
                ))}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </>
  );
}
